import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function safeRedirect(url: string): string {
  if (url.startsWith("/") && !url.startsWith("//")) return url;
  return "/app";
}

/**
 * Convert an iRacing rubber state label (or raw percentage) to a percentage 0-100.
 * iRacing reports track rubber as text like "moderate usage", "high usage", etc.
 * Returns null if the value cannot be interpreted.
 */
export function trackStateToPercent(value: string | null | undefined): number | null {
  if (value == null) return null;
  const trimmed = String(value).trim();
  if (!trimmed) return null;

  // Already a number (e.g. "75" or "75%")?
  const numericMatch = trimmed.match(/^(\d+(?:\.\d+)?)\s*%?$/);
  if (numericMatch) {
    const n = parseFloat(numericMatch[1]!);
    if (!Number.isNaN(n)) return Math.min(100, Math.max(0, Math.round(n)));
  }

  // Map iRacing text labels to approximate percentages
  const lower = trimmed.toLowerCase();
  if (lower.includes("clean") || lower === "none") return 0;
  if (lower.includes("slow") || lower.includes("low")) return 25;
  if (lower.includes("moderate") || lower.includes("medium")) return 50;
  if (lower.includes("heavy") || lower.includes("high")) return 75;
  if (lower.includes("maximum") || lower.includes("max")) return 100;
  return null;
}

/** Format track state as a percentage string like "50%". Falls back to "—". */
export function formatTrackState(value: string | null | undefined): string {
  const pct = trackStateToPercent(value);
  return pct != null ? `${pct}%` : "—";
}

/** Format an ISO datetime string to "Apr 11, 2026, 2:15 PM". Falls back to the raw value or "—". */
export function formatSessionDateTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return String(iso);
  return date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}
